import { useState, useEffect, useCallback, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TrendingUp,
  Users,
  AlertTriangle,
  Clock,
  Trophy,
  Target,
  Phone,
  Calendar,
  Loader2,
  RefreshCw,
} from 'lucide-react';
import { api } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';
import { PageHeader } from '../../components/ui';
import { formatDate, formatCurrency } from '../../utils/format';
import { StatCard, Panel, Badge, PanelLink } from '../../design-system';

// ==================== INTERFACES ====================

interface CrmMetricas {
  novosLeads: number;
  leadsGanhos: number;
  leadsPerdidos: number;
  leadsAbertos: number;
  taxaConversao: number | null;
}

interface FunilEtapa {
  etapa: string;
  cor: string;
  ordem: number;
  quantidade: number;
}

interface PerformanceVendedor {
  vendedorId: number;
  vendedorNome: string;
  leadsRecebidos: number;
  leadsGanhos: number;
  leadsPerdidos: number;
  leadsAbertos: number;
  taxaConversao: number | null;
  tempoMedioConversao: number | null;
}

interface LeadSemContato {
  leadId: number;
  leadNome: string;
  telefone: string | null;
  vendedorId: number | null;
  vendedorNome: string | null;
  etapaNome: string;
  etapaCor: string;
  diasParado: number;
  dataUltimoContato: string | null;
  dataCadastro: string;
}

interface TemperaturaLead {
  temperatura: string | null;
  quantidade: number;
}

interface VeiculoParado {
  id: number;
  placa: string;
  descricao: string;
  anoFab: number;
  anoMod: number;
  valor: number;
  dataEntrada: string;
  diasParado: number;
}

interface CrmDashboardCompleto {
  metricas: CrmMetricas;
  funil: FunilEtapa[];
  performance: PerformanceVendedor[];
  leadsSemContato: LeadSemContato[];
  temperaturas: TemperaturaLead[];
  estoqueParado: VeiculoParado[];
}

// ==================== COMPONENTES MEMORIZADOS ====================

interface LeadItemProps {
  lead: LeadSemContato;
  onClick: () => void;
}

const LeadItem = memo(function LeadItem({ lead, onClick }: LeadItemProps) {
  return (
    <button
      onClick={onClick}
      className="w-full rounded-xl border border-transparent p-3 text-left transition-colors hover:border-slate-200 hover:bg-slate-50"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate font-semibold text-slate-800">{lead.leadNome}</p>
          {lead.telefone && (
            <p className="mt-1 flex items-center gap-1 text-sm text-slate-400">
              <Phone className="h-3 w-3" />
              {lead.telefone}
            </p>
          )}
        </div>
        <div className="shrink-0 text-right">
          <span className="mt-1 inline-flex items-center gap-1.5 text-xs text-slate-500">
            <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: lead.etapaCor }} />
            {lead.etapaNome}
          </span>
          <p className="mt-1 text-xs font-semibold text-rose-600">
            {lead.diasParado} dias parado
          </p>
        </div>
      </div>
    </button>
  );
});

interface VeiculoRowProps {
  veiculo: VeiculoParado;
  onClick: () => void;
}

const VeiculoRow = memo(function VeiculoRow({ veiculo, onClick }: VeiculoRowProps) {
  return (
    <tr
      onClick={onClick}
      className="group cursor-pointer border-b border-slate-50 last:border-0 hover:bg-slate-50"
    >
      <td className="py-3 font-semibold text-slate-800 group-hover:text-primary-600">{veiculo.descricao}</td>
      <td className="py-3 font-mono text-slate-500">{veiculo.placa}</td>
      <td className="py-3 tabular-nums text-slate-500">{veiculo.anoFab}/{veiculo.anoMod}</td>
      <td className="py-3 tabular-nums text-slate-900">{formatCurrency(veiculo.valor)}</td>
      <td className="py-3">
        <span className="flex items-center gap-1 text-slate-500">
          <Calendar className="h-3 w-3 text-slate-400" />
          {formatDate(veiculo.dataEntrada)}
        </span>
      </td>
      <td className="py-3">
        <Badge tone={veiculo.diasParado >= 60 ? 'danger' : 'warning'}>
          {veiculo.diasParado} dias
        </Badge>
      </td>
    </tr>
  );
});

// ==================== COMPONENTE PRINCIPAL ====================

export function CrmDashboard() {
  const navigate = useNavigate();
  const { showError } = useNotification();

  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState<CrmDashboardCompleto | null>(null);
  const [periodo, setPeriodo] = useState('30');

  const fetchData = useCallback(async (signal?: AbortSignal) => {
    setIsLoading(true);
    try {
      const dataFim = new Date();
      const dataInicio = new Date();
      dataInicio.setDate(dataInicio.getDate() - parseInt(periodo));

      const params = new URLSearchParams({
        dataInicio: dataInicio.toISOString(),
        dataFim: dataFim.toISOString(),
        diasSemContato: '3',
        diasEstoqueParado: '30'
      });

      // Uma única chamada ao invés de 6
      const response = await api.get<CrmDashboardCompleto>(
        `/alertas/crm-dashboard-completo?${params}`,
        { signal }
      );

      setData(response.data);
    } catch (error: unknown) {
      if (error instanceof Error && error.name !== 'CanceledError') {
        showError('Erro ao carregar dados do CRM');
      }
    } finally {
      setIsLoading(false);
    }
  }, [periodo, showError]);

  useEffect(() => {
    const controller = new AbortController();
    fetchData(controller.signal);
    return () => controller.abort();
  }, [fetchData]);

  if (isLoading || !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  const { metricas, performance, leadsSemContato, estoqueParado } = data;

  return (
    <div className="space-y-6">
      <PageHeader title="Dashboard CRM" subtitle="Acompanhe a performance do time comercial">
        <div className="flex items-center gap-2">
          <select
            value={periodo}
            onChange={(e) => setPeriodo(e.target.value)}
            className="h-10 cursor-pointer rounded-xl border border-slate-200 bg-white px-3.5 text-sm font-semibold text-slate-600 transition-colors hover:border-slate-300 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
          >
            <option value="7">Últimos 7 dias</option>
            <option value="15">Últimos 15 dias</option>
            <option value="30">Últimos 30 dias</option>
            <option value="60">Últimos 60 dias</option>
            <option value="90">Últimos 90 dias</option>
          </select>
          <button
            onClick={() => fetchData()}
            title="Atualizar"
            className="grid h-10 w-10 place-items-center rounded-xl border border-slate-200 bg-white text-slate-500 transition-colors hover:border-slate-300 hover:bg-slate-50 hover:text-slate-700"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>
      </PageHeader>

      {/* Métricas Principais */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
        <StatCard
          title="Novos Leads"
          value={metricas.novosLeads}
          icon={<Users className="h-[18px] w-[18px]" />}
          delay={0}
        />
        <StatCard
          title="Ganhos"
          value={metricas.leadsGanhos}
          icon={<Trophy className="h-[18px] w-[18px]" />}
          tone="positive"
          delay={60}
        />
        <StatCard
          title="Perdidos"
          value={metricas.leadsPerdidos}
          icon={<AlertTriangle className="h-[18px] w-[18px]" />}
          tone={metricas.leadsPerdidos > 0 ? 'negative' : 'default'}
          delay={120}
        />
        <StatCard
          title="Em Aberto"
          value={metricas.leadsAbertos}
          icon={<Clock className="h-[18px] w-[18px]" />}
          delay={180}
        />
        <StatCard
          title="Conversão"
          value={metricas.taxaConversao !== null ? `${metricas.taxaConversao}%` : '-'}
          icon={<Target className="h-[18px] w-[18px]" />}
          delay={240}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Performance dos Vendedores */}
        <Panel title="Performance Vendedores" icon={<TrendingUp className="h-4 w-4" />}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 text-left text-[11px] font-bold uppercase tracking-wider text-slate-400">
                  <th className="pb-3 font-bold">Vendedor</th>
                  <th className="pb-3 text-center font-bold">Recebidos</th>
                  <th className="pb-3 text-center font-bold">Ganhos</th>
                  <th className="pb-3 text-center font-bold">Conversão</th>
                </tr>
              </thead>
              <tbody>
                {performance.map((v) => (
                  <tr key={v.vendedorId} className="border-b border-slate-50 last:border-0">
                    <td className="py-3 font-semibold text-slate-800">{v.vendedorNome}</td>
                    <td className="py-3 text-center tabular-nums text-slate-600">{v.leadsRecebidos}</td>
                    <td className="py-3 text-center font-bold tabular-nums text-emerald-600">{v.leadsGanhos}</td>
                    <td className="py-3 text-center">
                      {v.taxaConversao !== null ? (
                        <Badge
                          tone={
                            v.taxaConversao >= 30
                              ? 'success'
                              : v.taxaConversao >= 15
                                ? 'warning'
                                : 'danger'
                          }
                        >
                          {v.taxaConversao}%
                        </Badge>
                      ) : (
                        '-'
                      )}
                    </td>
                  </tr>
                ))}
                {performance.length === 0 && (
                  <tr>
                    <td colSpan={4} className="py-4 text-center text-slate-400">
                      Nenhum dado disponível
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Panel>

        {/* Leads sem Contato */}
        <Panel
          title="Leads sem Contato (+3 dias)"
          icon={<AlertTriangle className="h-4 w-4" />}
          action={<span className="text-xs font-semibold text-slate-400">{leadsSemContato.length} leads</span>}
        >
          <div className="max-h-64 space-y-1.5 overflow-y-auto">
            {leadsSemContato.slice(0, 10).map((lead) => (
              <LeadItem
                key={lead.leadId}
                lead={lead}
                onClick={() => navigate(`/crm/leads/${lead.leadId}`)}
              />
            ))}
            {leadsSemContato.length === 0 && (
              <p className="py-4 text-center text-sm text-slate-400">
                Nenhum lead sem contato
              </p>
            )}
          </div>
        </Panel>
      </div>

      {/* Estoque Parado */}
      {estoqueParado.length > 0 && (
        <Panel
          title="Estoque Parado (+30 dias)"
          icon={<Clock className="h-4 w-4" />}
          action={<PanelLink onClick={() => navigate('/veiculos')}>Ver Veículos</PanelLink>}
        >
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 text-left text-[11px] font-bold uppercase tracking-wider text-slate-400">
                  <th className="pb-3 font-bold">Veículo</th>
                  <th className="pb-3 font-bold">Placa</th>
                  <th className="pb-3 font-bold">Ano</th>
                  <th className="pb-3 font-bold">Valor</th>
                  <th className="pb-3 font-bold">Entrada</th>
                  <th className="pb-3 font-bold">Dias Parado</th>
                </tr>
              </thead>
              <tbody>
                {estoqueParado.slice(0, 10).map((v) => (
                  <VeiculoRow
                    key={v.id}
                    veiculo={v}
                    onClick={() => navigate(`/veiculos/${v.id}`)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}
    </div>
  );
}
